import { animated, AnimatedComponent, to } from '@react-spring/web';
import React from 'react';

import { AnimatableValue } from '../../utils/AnimatableValue';
import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { getRotateTransform } from '../../utils/getRotateTransform';
import { getTranslateTransform } from '../../utils/getTranslateTransform';

interface TextOwnProps {
  /**
   * The angle to rotate the text, if any.
   */
  angle?: number | string;
  /**
   * A left (x) offset for the text.
   */
  left?: AnimatableValue<number>;
  /**
   * A top (y) offset for the text.
   */
  top?: AnimatableValue<number>;
  /**
   * The text to display.
   */
  children: React.ReactNode;
  /**
   * Where the vertical anchor of the text should be placed. `start` references the top of the line;
   * `end` references the bottom (i.e., baseline).
   */
  verticalAnchor?: 'start' | 'middle' | 'end';
  /**
   * The font size of the text.
   */
  fontSize?: number;
  /**
   * The assumed height of a line of text to use for layout calculation purposes.
   */
  textHeight?: number;
  /**
   * The padding to add between wrapped lines.
   */
  linePadding?: number;
}

export type TextProps = ExtendAndOverride<
  TextOwnProps,
  Omit<React.ComponentPropsWithoutRef<AnimatedComponent<'text'>>, 'css'>
>;

const getDyMultiplier = (anchor: 'start' | 'middle' | 'end' | undefined) => {
  switch (anchor) {
    case 'start':
      return 1;
    case 'middle':
      return 0.5;
    default:
      return 0;
  }
};

const getTransform = (left: number, top: number, angle: string | number | undefined) =>
  [getTranslateTransform(left, top), getRotateTransform(angle)].filter(Boolean).join(' ');

export const Text: React.ForwardRefExoticComponent<TextProps & { ref?: React.Ref<SVGTextElement> }> = React.forwardRef<
  SVGTextElement,
  TextProps
>(
  (
    {
      angle,
      verticalAnchor,
      children,
      top = 0,
      left = 0,
      fontSize,
      textHeight = fontSize ?? 12,
      linePadding = 2,
      ...textProps
    },
    ref,
  ): JSX.Element => {
    const transform = to([left, top], (animatedLeft, animatedTop) => getTransform(animatedLeft, animatedTop, angle));

    const textLines = ([] as React.ReactNode[]).concat(children);

    // FIXME: figure out how to use this properly with multi-line text/angled text
    const dyMultiplier = getDyMultiplier(verticalAnchor);

    const getLineYOffset = (i: number) => {
      return textHeight * dyMultiplier + (linePadding + textHeight) * i;
    };

    const getLineXPos = (i: number) => {
      // FIXME: we are avoiding trigonometry by assuming any non-zero angle is 45 degrees
      if (angle) {
        return (textHeight + linePadding) * i;
      }

      return 0;
    };

    return (
      <animated.text transform={transform} fontSize={fontSize} {...textProps} ref={ref}>
        {textLines.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key -- index is best key available
          <tspan key={i} x={getLineXPos(i)} dy={getLineYOffset(i)}>
            {line}
          </tspan>
        ))}
      </animated.text>
    );
  },
);
